import React from "react";

import type { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { ExternalLink, PlayCircle, ShieldCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  type TinaSiteOption,
  extractKnownVenueIdFromOption,
  normalizeCity,
} from "@/pages/tina/runs/components/site-picker/utils";
import { siteSourceUrl } from "@/pages/tina/sites/lib/site-url";
import type { ManualUnblockMode, SiteRead } from "@/types/tina";

export interface UseSitesColumnsHandlers {
  onToggleEnabled: (name: string, next: boolean) => void;
  onDiscover: (name: string) => void;
  onEdit: (name: string) => void;
  onUnblock: (name: string) => void;
  busyName: string | null;
}

const MANUAL_UNBLOCK_LABELS: Record<ManualUnblockMode, string> = {
  recaptcha: "reCAPTCHA",
  login: "Login",
};

function manualUnblockMode(site: SiteRead): ManualUnblockMode | null {
  const mode = site.config?.["manual_unblock_mode"];
  return mode === "recaptcha" || mode === "login" ? mode : null;
}

function formatLastRun(value: string | null | undefined): string {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : "—";
}

export function useSitesColumns({
  onToggleEnabled,
  onDiscover,
  onEdit,
  onUnblock,
  busyName,
}: UseSitesColumnsHandlers): ColumnDef<SiteRead>[] {
  return React.useMemo<ColumnDef<SiteRead>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Site",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "url",
        header: "URL",
        cell: ({ row }) => {
          const href = siteSourceUrl(row.original);
          if (!href) return <span className="text-muted-foreground">—</span>;
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={href}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex max-w-[240px] items-center gap-1 align-middle text-blue-600 hover:underline dark:text-blue-400"
            >
              <span className="min-w-0 truncate">{href}</span>
              <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
            </a>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "city",
        accessorKey: "city",
        header: "City",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {normalizeCity(row.original.city)}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "x_strategy",
        accessorKey: "x_strategy",
        header: "Strategy",
        cell: ({ row }) => (
          <Badge variant="secondary">{row.original.x_strategy}</Badge>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "manual_unblock_mode",
        header: "Unblock",
        cell: ({ row }) => {
          const mode = manualUnblockMode(row.original);
          return mode ? (
            <Badge variant="destructive">{MANUAL_UNBLOCK_LABELS[mode]}</Badge>
          ) : null;
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "known_venue",
        header: "Known venue",
        cell: ({ row }) => (
          <span className="truncate font-mono text-xs text-muted-foreground">
            {extractKnownVenueIdFromOption(
              row.original as unknown as TinaSiteOption,
            ) ?? "—"}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "last_processed_at",
        accessorKey: "last_processed_at",
        header: "Last run",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatLastRun(row.original.last_processed_at)}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "enabled",
        accessorKey: "enabled",
        header: "Enabled",
        cell: ({ row }) => (
          <Switch
            checked={row.original.enabled}
            disabled={busyName === row.original.name}
            onCheckedChange={(next) => onToggleEnabled(row.original.name, next)}
            aria-label={`Toggle ${row.original.name}`}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: "Actions",
        size: 300,
        cell: ({ row }) => (
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(row.original.name)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDiscover(row.original.name)}
            >
              <PlayCircle className="mr-1 h-4 w-4" />
              Discover
            </Button>
            {manualUnblockMode(row.original) ? (
              <Button
                size="sm"
                variant="ghost"
                title="Manual unblock"
                onClick={() => onUnblock(row.original.name)}
              >
                <ShieldCheck className="mr-1 h-4 w-4" />
                Unblock
              </Button>
            ) : null}
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [onToggleEnabled, onDiscover, onEdit, onUnblock, busyName],
  );
}
